import { useEffect, useState } from "react";
import { api } from "../api";
import { ErrorBanner, Skeleton } from "../components";

// The last-loaded gallery response, kept at module scope so returning to the
// page shows it at once (and refreshes in the background) instead of flashing
// the loading skeleton on every navigation, like the Chats page.
let exploreCache = null;

// A compass, the empty-gallery illustration.
const CompassIcon = () => (
  <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="32" cy="32" r="24" />
    <path d="M42 22 36 36 22 42 28 28z" />
    <circle cx="32" cy="32" r="2.5" fill="currentColor" stroke="none" />
  </svg>
);

// The public app gallery: public apps their owners chose to list. Behind the
// login (a members' gallery, not the open internet); the nav only shows the link
// when the instance has the gallery on, but the page states it plainly too.
const Explore = () => {
  const [data, setData] = useState(exploreCache);
  const [error, setError] = useState("");
  useEffect(() => {
    let live = true;
    api
      .get("/api/explore")
      .then((d) => {
        exploreCache = d;
        if (live) setData(d);
      })
      .catch((e) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, []);
  const apps = data && data.enabled ? data.apps : [];
  // With nothing to show, the centered empty state carries the whole message, so
  // the page header and hint would only crowd it.
  const empty = data && data.enabled && apps.length === 0;
  return (
    <>
      {!empty && (
        <>
          <div className="page-header">
            <h1>Explore</h1>
          </div>
          <p className="hint">Public apps that members of this instance have chosen to share.</p>
        </>
      )}
      <ErrorBanner message={error} onDismiss={() => setError("")} />
      {data === null && !error && <Skeleton card rows={3} label="Loading apps..." />}
      {data && !data.enabled && <p className="empty">The app gallery is turned off on this instance.</p>}
      {data && data.enabled && apps.length === 0 && (
        <div className="explore-empty">
          <div className="explore-empty-icon" aria-hidden="true">
            <CompassIcon />
          </div>
          <h2>Nothing to explore yet</h2>
          <p>
            Public apps show up here once an owner lists one. Open an app, choose{" "}
            <strong>Share</strong>, and set its visibility to <strong>Listed</strong> to add it
            to the gallery.
          </p>
        </div>
      )}
      {apps.length > 0 && (
        <div className="explore-grid">
          {apps.map((a) => (
            <a key={a.name} className="explore-card" href={a.url} target="_blank" rel="noreferrer">
              {/* The stored preview, when the instance takes screenshots and one
                  exists. It is decorative: the card already names the app, and a
                  shot that fails to load simply hides itself. */}
              {a.has_shot && (
                <span className="explore-shot">
                  <img
                    src={`/api/explore/${encodeURIComponent(a.name)}/preview.png`}
                    alt=""
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.parentElement.style.display = "none";
                    }}
                  />
                </span>
              )}
              <span className="explore-name">{a.name}</span>
              {a.owner_name && <span className="explore-owner">by {a.owner_name}</span>}
              <span className="explore-desc">{a.description || "No description yet"}</span>
              <span className="explore-host">{a.domain || a.url.replace(/^https?:\/\//, "")}</span>
            </a>
          ))}
        </div>
      )}
    </>
  );
};

export default Explore;
